import { BaseCartItem } from '@pegasus/cart';
import { Config, ImageSize } from '@browsing/config';
import { getQueryParam } from '@browsing/utils';

import {
    ADD_CART_MAX_SIMPLES_PER_CONFIG,
    ADD_CART_MAX_SIMPLES_PER_CONFIG_REACHED_QTY_ADJUSTED,
    ADD_CART_MAX_30_SIMPLES_IN_CART,
    ADD_CART_STOCK_NOT_FOUND,
    ADD_CART_MAX_AVAILABLE_STOCK_REACHED_QTY_ADJUSTED,
    ADD_CART_NOT_COMBINABLE_ADD_ITEM_NOT_COMBINABLE,
    ADD_CART_SIZE_SELECTION_WARNING,
    ADD_CART_VOUCHER_DISABLED,
    ADD_CART_NOT_COMBINABLE_NOT_COMBINABLE_IN_CART,
    ADD_CART_SIZE_SELECTION_ERROR,
    TRACK_RECO_NO_RECO,
} from '@client/config/constants';
import { AddToCartError, CartErrorItem } from './types';

export const transformCartItems = <T extends BaseCartItem>(items: T[] = [], config: Config): T[] =>
    items.map((item: T) => ({
        ...item,
        imageUrl: config.getImage(item.media[0].path || item.imageUrl, ImageSize.Thumbnail),
        altText: item.media[0].alt_text || item.name,
    }));

type ErrorCode =
    | typeof ADD_CART_MAX_SIMPLES_PER_CONFIG
    | typeof ADD_CART_MAX_SIMPLES_PER_CONFIG_REACHED_QTY_ADJUSTED
    | typeof ADD_CART_MAX_30_SIMPLES_IN_CART
    | typeof ADD_CART_STOCK_NOT_FOUND
    | typeof ADD_CART_MAX_AVAILABLE_STOCK_REACHED_QTY_ADJUSTED
    | typeof ADD_CART_NOT_COMBINABLE_ADD_ITEM_NOT_COMBINABLE
    | typeof ADD_CART_SIZE_SELECTION_WARNING
    | typeof ADD_CART_VOUCHER_DISABLED
    | typeof ADD_CART_NOT_COMBINABLE_NOT_COMBINABLE_IN_CART
    | typeof ADD_CART_SIZE_SELECTION_ERROR;

interface AddToCartIncomingError {
    code: ErrorCode;
    message: string;
    details: null | {
        existingItems?: CartErrorItem[];
    };
}

export const manageAddToCartError = ({
    code,
    message,
    details,
}: AddToCartIncomingError): AddToCartError => {
    const { existingItems = [] } = details || {};
    const result = {
        isSizeOverlayOpen: false,
        simplesInCart: [] as CartErrorItem[],
        maximumQuantityOfArticleReached: false,
        stockNotFound: false,
        warningMessage: '',
        errorMessage: '',
    };

    switch (code) {
        case ADD_CART_SIZE_SELECTION_WARNING:
            return {
                ...result,
                simplesInCart: existingItems,
                isSizeOverlayOpen: true,
            };
        case ADD_CART_MAX_SIMPLES_PER_CONFIG:
        case ADD_CART_MAX_30_SIMPLES_IN_CART:
            return {
                ...result,
                maximumQuantityOfArticleReached: true,
            };
        case ADD_CART_STOCK_NOT_FOUND:
            return { ...result, stockNotFound: true };
        case ADD_CART_VOUCHER_DISABLED:
        case ADD_CART_NOT_COMBINABLE_ADD_ITEM_NOT_COMBINABLE:
        case ADD_CART_NOT_COMBINABLE_NOT_COMBINABLE_IN_CART:
        case ADD_CART_SIZE_SELECTION_ERROR:
        case ADD_CART_MAX_SIMPLES_PER_CONFIG_REACHED_QTY_ADJUSTED:
        case ADD_CART_MAX_AVAILABLE_STOCK_REACHED_QTY_ADJUSTED:
            return { ...result, errorMessage: message };
        default:
            return result;
    }
};

export const getAdditionalInformation = () => {
    return {
        reco: getQueryParam('reco') || TRACK_RECO_NO_RECO,
    };
};
